/**
 * Which artifact the web workspace shows in its canvas panel (Gemini's
 * immersive panel): at most one at a time, docked beside the chat or
 * expanded to the full width. A module-level store like the composer
 * preferences, because the panel is drawn by the protected shell while the
 * cards that open it live deep inside the chat thread.
 */
import { useSyncExternalStore } from 'react';

export type ArtifactPanelOrigin = 'chat' | 'route';

/**
 * The view an opener asks for: a building card's Open pill shows a page's
 * code while it is built (plan 7.2); the suggestions card's Open to review
 * opens the editor (plan 7.4). Absent means the preview.
 */
export type ArtifactPanelView = 'preview' | 'code' | 'edit';

/** Gemini's `mobile-device` breakpoint for the canvas: below it the panel is the screen. */
export const ARTIFACT_PANEL_DOCK_MIN_WIDTH = 900;
export const ARTIFACT_PANEL_DOCK_QUERY = `(min-width: ${ARTIFACT_PANEL_DOCK_MIN_WIDTH}px)`;

export type ArtifactPanelState = {
  artifactId: string | null;
  /** Full width: the chat thread hides and only its composer stays. */
  expanded: boolean;
  /** Opened from a card in the conversation, or by the /artifacts/[id] link. */
  origin: ArtifactPanelOrigin | null;
  /** The view the opener asked for; absent means the preview. */
  view?: ArtifactPanelView;
  /** Counts building opens, so asking for the same view again still switches to it. */
  viewRequest?: number;
  /** Opened by the writer's first build (auto-open), not by the reader. */
  auto?: boolean;
  /**
   * The version on screen (plan 7.5); null or absent means the head. Every
   * open shows the latest version.
   */
  versionId?: string | null;
};

export type ArtifactPanelAction =
  | { type: 'open'; artifactId: string }
  | { type: 'openRoute'; artifactId: string }
  | { type: 'openBuilding'; artifactId: string; view: ArtifactPanelView; auto?: boolean }
  | { type: 'close' }
  | { type: 'toggleExpanded' }
  | { type: 'setVersion'; artifactId: string; versionId: string | null };

export const CLOSED_ARTIFACT_PANEL: ArtifactPanelState = Object.freeze({
  artifactId: null,
  expanded: false,
  origin: null,
});

export function artifactPanelReducer(
  state: ArtifactPanelState,
  action: ArtifactPanelAction,
): ArtifactPanelState {
  switch (action.type) {
    case 'open':
      if (!action.artifactId) return state;
      // Another artifact replaces the content of the open panel and keeps its
      // width; a first open docks beside the chat.
      return {
        artifactId: action.artifactId,
        expanded: state.artifactId ? state.expanded : false,
        origin: 'chat',
      };
    case 'openBuilding':
      if (!action.artifactId) return state;
      // Like `open`, and the panel starts on the asked view while it builds.
      return {
        artifactId: action.artifactId,
        expanded: state.artifactId ? state.expanded : false,
        origin: 'chat',
        view: action.view,
        viewRequest: (state.viewRequest ?? 0) + 1,
        ...(action.auto ? { auto: true } : {}),
      };
    case 'openRoute':
      if (!action.artifactId) return state;
      // The owned link opens straight into the full-width view.
      return { artifactId: action.artifactId, expanded: true, origin: 'route' };
    case 'toggleExpanded':
      return state.artifactId ? { ...state, expanded: !state.expanded } : state;
    case 'setVersion':
      if (state.artifactId !== action.artifactId || (state.versionId ?? null) === action.versionId) return state;
      return { ...state, versionId: action.versionId };
    case 'close':
      return CLOSED_ARTIFACT_PANEL;
  }
}

let state: ArtifactPanelState = CLOSED_ARTIFACT_PANEL;
const listeners = new Set<() => void>();
/** When an open panel was last closed, for the auto-open rule (plan 7.2). */
let lastClosedAt: number | null = null;

function dispatch(action: ArtifactPanelAction) {
  const next = artifactPanelReducer(state, action);
  if (next === state) return;
  if (action.type === 'close' && state.artifactId) lastClosedAt = Date.now();
  state = next;
  for (const listener of listeners) listener();
}

export function readArtifactPanel(): ArtifactPanelState {
  return state;
}

export function subscribeArtifactPanel(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export const openArtifactPanel = (artifactId: string) => dispatch({ type: 'open', artifactId });
export const openArtifactPanelFromRoute = (artifactId: string) =>
  dispatch({ type: 'openRoute', artifactId });
export const openArtifactPanelBuilding = (
  artifactId: string,
  view: ArtifactPanelView,
  options: { auto?: boolean } = {},
) => dispatch({ type: 'openBuilding', artifactId, view, auto: options.auto });
export const closeArtifactPanel = () => dispatch({ type: 'close' });
/** Show another version of the open artifact (null: back to the latest). */
export const setArtifactPanelVersion = (artifactId: string, versionId: string | null) =>
  dispatch({ type: 'setVersion', artifactId, versionId });
/** Open the artifact in its editor (the suggestions card's Open to review). */
export const openArtifactPanelEditing = (artifactId: string) =>
  dispatch({ type: 'openBuilding', artifactId, view: 'edit' });

/** When the panel was last closed (null when never), read by the auto-open rule. */
export function readArtifactPanelLastClosedAt(): number | null {
  return lastClosedAt;
}
export const toggleArtifactPanelExpanded = () => dispatch({ type: 'toggleExpanded' });

export function resetArtifactPanelForTests() {
  state = CLOSED_ARTIFACT_PANEL;
  lastClosedAt = null;
  listeners.clear();
}

export function useArtifactPanel(): ArtifactPanelState {
  return useSyncExternalStore(subscribeArtifactPanel, readArtifactPanel, () => CLOSED_ARTIFACT_PANEL);
}
